import { useEffect } from "react";
import { useSelector } from "react-redux";

import {
  setAnnouncementState,
  setMaintenanceState,
  setNoticesLoaded,
} from "../store/reducers/appNoticeSlice";
import { useAppDispatch } from "../store/store";
import { envConfig } from "../utils/config-utils";
import {
  resolveAnnouncement,
  resolveMaintenance,
} from "../webclients/appNotice.client";

interface CountrySliceShape {
  country?: string;
}

const selectCountry = (state: { country?: CountrySliceShape }) =>
  state.country?.country ?? "senegal";

/**
 * Loads the MAINTENANCE and ANNOUNCEMENT feature flags from the BFF once on mount.
 *
 * - Runs both API calls in parallel.
 * - Fail-open: on any API error the flags default to `false` (loaded=true) so the
 *   app keeps working even if the public app-config endpoint is unreachable.
 * - Reads the active environment from `envConfig.env` (host-derived when
 *   VITE_ENV is missing) and the country from the existing `country` Redux
 *   slice (defaults to "senegal").
 */
export const useAppNotices = () => {
  const dispatch = useAppDispatch();
  const country = useSelector(selectCountry);

  useEffect(() => {
    const environment = envConfig.env;

    let cancelled = false;

    const loadFlags = async () => {
      try {
        const [maintenanceResponse, announcementResponse] = await Promise.all([
          resolveMaintenance(environment).catch(() => null),
          resolveAnnouncement(environment, country).catch(() => null),
        ]);

        if (cancelled) return;

        // Fail-open: a null response (network/API error) becomes `false`.
        const maintenanceEnabled = maintenanceResponse?.data?.enabled === true;
        const announcementEnabled =
          announcementResponse?.data?.enabled === true;
        const announcementMessage = {
          en: announcementResponse?.data?.messageEn ?? null,
          fr: announcementResponse?.data?.messageFr ?? null,
          ar: announcementResponse?.data?.messageAr ?? null,
        };
        const announcementBgColor = announcementResponse?.data?.bgColor ?? null;

        dispatch(setMaintenanceState(maintenanceEnabled));
        dispatch(
          setAnnouncementState({
            enabled: announcementEnabled,
            message: announcementMessage,
            bgColor: announcementBgColor,
          })
        );
      } catch {
        if (cancelled) return;
        // Defensive fail-open: ensure both flags are marked loaded=false-enabled.
        dispatch(setMaintenanceState(false));
        dispatch(
          setAnnouncementState({
            enabled: false,
            message: { en: null, fr: null, ar: null },
          })
        );
        dispatch(setNoticesLoaded());
      }
    };

    loadFlags();

    return () => {
      cancelled = true;
    };
    // We intentionally run this only when the country changes (and on mount).
  }, [country, dispatch]);
};

export default useAppNotices;
